import Table from '@/components/Table'
import { TableData } from '@/components/Table/type'
import { useParameterConferenceHubQuery } from '@/queries/useParameterConferenceHubQuery'
import { ParameterConferenceHub } from '@/types/ParameterConferenceHub'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'
import DadosMestreHeader from '../components/DadosMestreHeader' 
import DadosMestreHeaderActions from '../components/DadosMestreHeaderActions' 
import DefaultColumn from '@/components/Table/components/DefaultColumn'
import { useTable } from '@/hooks/useTable'
import { formatDateToString } from '@/utils/date'
import DeleteDialog from './components/DeleteDialog'
import EditDialog from './components/EditDialog'
import AddDialog from './components/AddDialog'
import TableRowActions from '@/components/Table/components/TableRowActions'

export type ParameterConferenceHubTable = TableData<ParameterConferenceHub>

const columnHelper = createColumnHelper<ParameterConferenceHub>()

const ProcessHubParameterConferenceHub = () => {
    const {
        table, 
        selectedRow,
        selectedRows,
        isAddDialogOpen,
        isDeleteDialogOpen,
        isEditDialogOpen, 
        onAddDialogClose,
        onClickAdd,
        onClickDelete,
        onClickEdit,
        onClickMultiDelete,
        onDeleteDialogClose,
        onEditDialogClose,  
        setTable, 
    } = useTable<ParameterConferenceHub>('')
  
    const { data, isFetching, isPending, refetch } = useParameterConferenceHubQuery()
    
    const memoData = useMemo(() => data || [], [data])
    const memoColumns = useMemo(
        () => [
            columnHelper.accessor('DS_ABREVIATURA_FORNECEDOR', {
                id: 'DS_ABREVIATURA_FORNECEDOR',
                header: 'Fornecedor',
                size: 160,
            }),  
            columnHelper.accessor('DS_ABREVIATURA_CLIENTE', {
                id: 'DS_ABREVIATURA_CLIENTE',
                header: 'Cliente',
                size: 160,
            }),  
            columnHelper.accessor(({ SK_TEMPO_VIGENCIA_INICIAL }) =>
                SK_TEMPO_VIGENCIA_INICIAL
                    ? formatDateToString(SK_TEMPO_VIGENCIA_INICIAL)
                    : '', {
                sortingFn: (rowA, rowB, _columnId) => {  
                    const statusA = Number(rowA.original[_columnId])
                    const statusB = Number(rowB.original[_columnId])
                    return statusA < statusB ? 1 : -1
                },
                id: 'SK_TEMPO_VIGENCIA_INICIAL',
                header: 'Vigência Inicial',
                size: 140,
            }), 
            columnHelper.accessor(({ SK_TEMPO_VIGENCIA_FINAL }) =>
                SK_TEMPO_VIGENCIA_FINAL
                    ? formatDateToString(SK_TEMPO_VIGENCIA_FINAL)
                    : '', {
                sortingFn: (rowA, rowB, _columnId) => {  
                    const statusA = Number(rowA.original[_columnId])
                    const statusB = Number(rowB.original[_columnId])
                    return statusA < statusB ? 1 : -1
                },
                id: 'SK_TEMPO_VIGENCIA_FINAL',
                header: 'Vigência Final',
                size: 130,
            }), 
            columnHelper.display({
                id: 'Ações',
                cell: ({ row }) => { 
                    return (
                        (
                            <TableRowActions
                                onClickDelete={() => onClickDelete(row)}
                                onClickEdit={() => onClickEdit(row)}
                            />
                        )
                    )
                },
                size: 60,
                enableColumnFilter: false,
                enableSorting: false,
            }),
        ],
        []
    )
    
    return (
        <div className="w-full h-full p-4 overflow-hidden">
            <Table<ParameterConferenceHubTable>
                data={memoData}
                columns={memoColumns}
                getRowId={(row) => row.SK_PARAMETRO_CONFERENCIA_HUB.toString()}
                isLoading={isPending}
                isFetching={isFetching}
                getTableInstance={(table) => setTable(table)} 
                tableHeader={
                    <DadosMestreHeader
                        title={`Parâmetros de Conferência Hub`}
                        onDelete={onClickMultiDelete}
                        selectedRows={
                            table
                                ?.getFilteredRowModel()
                                .flatRows.filter((row) => selectedRows[row.id])
                                .length
                        }
                    />
                }
                tableActions={
                    <DadosMestreHeaderActions 
                        onRefech={refetch}
                        onAdd={onClickAdd}
                    />
                } 
                defaultColumn={{
                    cell: ({ getValue }) => (
                        <DefaultColumn>{getValue() as string}</DefaultColumn>
                    ),
                }}
            /> 
            {isDeleteDialogOpen && (
                <DeleteDialog
                    ids={
                        selectedRow
                            ? [selectedRow.original.SK_PARAMETRO_CONFERENCIA_HUB]
                            : table
                            ? table
                                  .getFilteredRowModel()
                                  .flatRows.filter(
                                      (row) => selectedRows[row.id]
                                  )
                                  .map((row) => row.original.SK_PARAMETRO_CONFERENCIA_HUB)
                            : []
                    }
                    isOpen={isDeleteDialogOpen}
                    onClose={onDeleteDialogClose}
                />
            )}
            {selectedRow && (
                <EditDialog
                    row={selectedRow}
                    isOpen={isEditDialogOpen}
                    onClose={onEditDialogClose}
                />
            )}
            <AddDialog isOpen={isAddDialogOpen} onClose={onAddDialogClose} /> 
        </div>
    )
}

export default ProcessHubParameterConferenceHub
